import React, { useCallback } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { SortDirection } from '@material-ui/core';
import { format } from 'date-fns';

import { useTranslation } from 'hooks/useTranslation';
import { t } from 'locales/i18n';
import { IdType } from 'ts/IdType';
import { Tabs } from 'shared/tabs';

import { PaginatedWrappedComponentProps, withPagination } from 'features/app/hocs/withPagination';
import { Table } from 'features/app/components/Table';
import { Paper } from 'features/app/components/Paper';
import { TableCellDef, TableColDef } from 'features/app/ts/Table';
import { PartnersBalanceTransactionsFilters } from 'features/finance/ts/PartnerBalanceTransactionFilters';
import { PartnerBalanceTransaction, TopUpTransactionStatusType } from 'features/finance/ts/PartnerBalanceTransaction';
import { TopUpTransactionStatus } from 'features/finance/components/TopUpTransactionStatus';

import { useStyles } from './styles';

const columns: TableColDef[] = [
  { field: 'id', label: t('topUpHistoryColumns.topUpId', { ns: 'finance' }) },
  { field: 'date', label: t('topUpHistoryColumns.date', { ns: 'finance' }), sortable: true },
  { field: 'user', label: t('topUpHistoryColumns.user', { ns: 'finance' }) },
  { field: 'type', label: t('topUpHistoryColumns.type', { ns: 'finance' }) },
  { field: 'amount', label: t('topUpHistoryColumns.amount', { ns: 'finance' }) },
  { field: 'status', label: t('topUpHistoryColumns.status', { ns: 'finance' }) },
];

enum TopUpTransactionStatusReadableNames {
  COMPLETED = 'Completed',
  FAILED = 'Failed',
}

const cells: TableCellDef[] = [
  {
    field: 'id',
  },
  {
    field: 'date',
    component: ({ item }) => {
      return <Typography>{format(new Date(item as string), 'LLL do yyyy p zzz').replace('GMT', 'UTC')}</Typography>;
    },
  },
  {
    field: 'user',
  },
  {
    field: 'type',
  },
  {
    field: 'amount',
  },
  {
    field: 'status',
    component: ({ item }) => (
      <TopUpTransactionStatus
        colorScheme={TopUpTransactionStatusType[item as TopUpTransactionStatusType]}
        label={TopUpTransactionStatusReadableNames[item as TopUpTransactionStatusType]}
      />
    ),
  },
];

const statusFilterTabs = [
  { value: '', label: t('topUpHistoryFilters.tabs.all', { ns: 'finance' }) },
  { value: TopUpTransactionStatusType.COMPLETED, label: t('topUpHistoryFilters.tabs.completed', { ns: 'finance' }) },
  { value: TopUpTransactionStatusType.FAILED, label: t('topUpHistoryFilters.tabs.failed', { ns: 'finance' }) },
];

const TAB_ACTIVE_COLOR = '#1921FA';

interface MappedTopUpHistoryItem {
  id?: IdType;
  date?: string;
  user?: string;
  type?: string;
  amount?: string;
  transaction_status?: TopUpTransactionStatusType;
}

interface TopUpHistoryProps {}

const topUpHistory = (props: TopUpHistoryProps) => {
  const classes = useStyles();

  const { filters, setFilters, data, isLoading } = props as PaginatedWrappedComponentProps<
    PartnerBalanceTransaction,
    PartnersBalanceTransactionsFilters
  >;

  const { t } = useTranslation('finance');

  const handleCardStatusChange = (_: unknown, value: TopUpTransactionStatusType) => {
    setFilters({ ...filters, transaction_status: value });
  };

  const handleSortChange = useCallback(
    (_: string, orderBy) => {
      setFilters({
        ...filters,
        order_by: 'created_at',
        ordering: orderBy?.toUpperCase() as PartnersBalanceTransactionsFilters['ordering'],
      });
    },
    [filters, setFilters]
  );

  const mappedData: MappedTopUpHistoryItem[] = React.useMemo(() => {
    return (
      data?.map(topUpTransaction => {
        const { amount, transferred_by, original_currency, transaction_status, created_at, id } = topUpTransaction;

        return {
          id,
          date: created_at,
          user: transferred_by,
          amount: `${amount} ${original_currency}`,
          type: 'Credit card',
          status: transaction_status,
        };
      }) ?? []
    );
  }, [data]);

  return (
    <Box className={classes.topUpHistoryContainer}>
      <Paper className={classes.topUpHistoryTitleContainer}>
        <Typography variant="h4" className={classes.topUpHistoryTitle}>
          {t('topUpHistory')}
        </Typography>
      </Paper>

      <Box className={classes.topUpHistoryStatusesFilter}>
        <Tabs
          onChange={handleCardStatusChange}
          value={filters.transaction_status ?? ''}
          tabs={statusFilterTabs}
          activeTabColor={TAB_ACTIVE_COLOR}
        />
      </Box>

      <Table
        columns={columns}
        cells={cells}
        data={mappedData ?? []}
        initialLoading={isLoading}
        empty={t('empty')}
        onSort={handleSortChange}
        orderBy="created_at"
        sortDirection={filters?.ordering?.toLowerCase() as unknown as SortDirection}
      />
    </Box>
  );
};

export const TopUpHistory = withPagination(
  {
    getEndpoint: '/partners/top-up-balance-by-credit-card/transactions/',
    queryId: 'top-up-history',
    pageSize: 8,
    defaultFilters: {
      order_by: 'created_at',
      ordering: 'DESC',
    },
  },
  topUpHistory
);
